import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.login(username, password);
      if (response.success) {
        toast.success('Login successful!');
        const user = authService.getCurrentUser();
        if (user?.role === 'EnrollmentOfficer' || user?.role === 'SecurityOfficer') {
          navigate('/employees');
        } else if (user?.role === 'SuperAccountant') {
          navigate('/companies');
        } else if (user?.role === 'SuperSupport') {
          navigate('/tickets');
        } else {
          // B2 fix (Jul 2026) — post-login lands on Dashboard for all
          // standard roles. /home tile canvas is still accessible via
          // sidebar but is no longer the default landing (KPI tiles
          // show broken `—` values and the Welcome banner is noisy).
          navigate('/dashboard');
        }
      } else {
        toast.error('Login failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A1931 0%, #0d2347 50%, #0A1931 100%)' }}>
      <div className="w-full max-w-md">
        {/* Logo - Clickable to Ajinkya Technologies */}
        <div className="text-center mb-8">
          <a href="https://www.ajinkyatechnologies.in" target="_blank" rel="noopener noreferrer" data-testid="login-logo-link">
            <img 
              src="/logo-wordmark-dark.png"
              alt="Ankastra - AI Attendance & Workforce Management"
              className="h-24 mx-auto mb-4 cursor-pointer hover:scale-105 transition-transform"
              style={{ filter: 'drop-shadow(0 0 16px rgba(212,175,55,0.25))' }}
            />
          </a>
          <p className="text-sm" style={{ color: '#5EC3EB' }}>Admin Dashboard Login</p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl shadow-2xl p-8 border" style={{ background: 'rgba(13, 35, 71, 0.85)', borderColor: 'rgba(94,195,235,0.15)', backdropFilter: 'blur(12px)' }}>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#5EC3EB' }}>
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-lg transition text-white placeholder-gray-400 focus:outline-none focus:ring-2"
                style={{ background: 'rgba(10, 25, 49, 0.8)', borderColor: 'rgba(94,195,235,0.2)', border: '1px solid rgba(94,195,235,0.2)', focusRingColor: '#22A7F0' }}
                placeholder="Enter your username"
                required
                data-testid="username-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#5EC3EB' }}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 rounded-lg transition text-white placeholder-gray-400 focus:outline-none focus:ring-2"
                  style={{ background: 'rgba(10, 25, 49, 0.8)', border: '1px solid rgba(94,195,235,0.2)' }}
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-white"
              style={{ background: 'linear-gradient(135deg, #22A7F0, #62F8E7)', boxShadow: '0 4px 20px rgba(34,167,240,0.3)' }}
              data-testid="login-button"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-3">
          <button onClick={() => navigate('/forgot-password')} data-testid="forgot-password-link"
            className="hover:text-white text-sm underline underline-offset-4 transition-colors" style={{ color: '#5EC3EB' }}>
            Forgot Password?
          </button>
          <br />
          <button onClick={() => navigate('/employee')} data-testid="employee-portal-link"
            className="hover:text-white text-sm underline underline-offset-4 transition-colors" style={{ color: '#5EC3EB' }}>
            Employee Self-Service Portal
          </button>
          <p className="text-sm mt-1" style={{ color: 'rgba(94,195,235,0.6)' }}>
            <a href="https://www.ajinkyatechnologies.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="login-ajinkya-link">
              Built by Ajinkya Technologies
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
